import { NextImage } from '@apeiron/temp'
import { Box, keyframes, Modal, styled } from '@mui/material'
import { useEffect, useState } from 'react'

// Spin animation with cubic-bezier for acceleration
const spin = keyframes`
  0% {
    transform: rotate(0deg);
    animation-timing-function: cubic-bezier(0.7, 0, 1.0, 1.0);
  }
  100% {
    transform: rotate(3600deg);
  }
`

const explodeAnim = keyframes`
  0% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: scale(3) rotate(600deg);
    opacity: 0;
  }
`

const LootBoxContainer = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
`

const LootBoxImage = styled(NextImage)`
  aspect-ratio: 1;
  width: 300px;
`

// Spinning box with cubic-bezier acceleration
const SpinningBox = styled(Box)`
  width: 300px;
  height: 300px;
  animation: ${spin} 3s cubic-bezier(0.1, 0.7, 1, 0.1) forwards;
  will-change: transform;
`

const ExplodingBox = styled(Box)`
  width: 300px;
  height: 300px;
  animation: ${explodeAnim} 0.5s forwards;
  will-change: transform, opacity;
`

const VipPassOpenBoxAnimation = ({ isOpen, boxImage }: LootBoxOpenerProps) => {
  const [animationPhase, setAnimationPhase] = useState<
    'idle' | 'spinning' | 'exploding' | 'reward'
  >('idle')

  useEffect(() => {
    if (isOpen) {
      setAnimationPhase('spinning')

      const spinningTime = 3000

      const explosionTime = 500

      const explosionTimer = setTimeout(() => {
        setAnimationPhase('exploding')
      }, spinningTime)

      const rewardTimer = setTimeout(() => {
        setAnimationPhase('reward')
      }, spinningTime + explosionTime)

      return () => {
        clearTimeout(explosionTimer)
        clearTimeout(rewardTimer)
      }
    } else {
      setAnimationPhase('idle')
    }
  }, [isOpen])

  // Render the appropriate box based on animation phase
  const renderAnimatedBox = () => {
    switch (animationPhase) {
      case 'spinning':
        return (
          <SpinningBox>
            <LootBoxImage src={boxImage} />
          </SpinningBox>
        )
      case 'exploding':
        return (
          <ExplodingBox>
            <LootBoxImage src={boxImage} />
          </ExplodingBox>
        )
      default:
        return null
    }
  }

  if (!isOpen) return null

  return (
    <Modal open={isOpen}>
      <LootBoxContainer>
        {animationPhase !== 'reward' && renderAnimatedBox()}
      </LootBoxContainer>
    </Modal>
  )
}

type LootBoxOpenerProps = {
  isOpen: boolean
  boxImage: string
}

export default VipPassOpenBoxAnimation
